import { createRoot } from 'react-dom/client';
import { QRCodeSVG } from 'qrcode.react';
import { MAX_QR_STRING_LENGTH } from '../../../shared/utils/walletPassQrPayload';

export { MAX_QR_STRING_LENGTH };

const LOADING_HTML =
    '<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Loading…</title></head><body><p style="font-family:system-ui,sans-serif;padding:24px">Loading QR code…</p></body></html>';

/**
 * Open a blank tab during the user gesture (before async work) so popups are not blocked.
 * @returns {Window | null}
 */
export function openWalletQrLoadingTab() {
    const win = window.open('about:blank', '_blank');
    if (!win) {
        return null;
    }
    try {
        win.document.open();
        win.document.write(LOADING_HTML);
        win.document.close();
    } catch {
        // Tab may still be usable once the pass URL is ready
    }
    return win;
}

/** Return focus to the merchant dashboard after opening the QR tab. */
export function focusMerchantAppTab() {
    try {
        window.focus();
    } catch {
        // Some browsers restrict focus(); non-fatal
    }
}

/**
 * @param {Window | null | undefined} win
 */
export function closeWalletQrTab(win) {
    if (!win || win.closed) {
        return;
    }
    try {
        win.close();
    } catch {
        // Non-fatal
    }
}

const BASE_STYLE = `
  * { box-sizing: border-box; }
  body {
    margin: 0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    background: #f8fafc;
    color: #0f172a;
    padding: 24px;
  }
  h1 { font-size: 1.25rem; font-weight: 600; margin: 0 0 20px; text-align: center; }
  .card {
    background: #fff;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.08);
    border: 1px solid #e2e8f0;
  }
  img { display: block; max-width: 100%; height: auto; }
  p { max-width: 28rem; line-height: 1.5; margin: 0 0 12px; text-align: center; }
  a {
    color: #2563eb;
    font-weight: 600;
    word-break: break-all;
  }
`;

/**
 * @param {Window | null} win
 * @param {{ qr: string | null, passAddUrl?: string | null, heading?: string }} opts
 * @returns {boolean} whether the window was updated
 */
export function renderWalletQrIntoWindow(win, { qr, passAddUrl = null, heading = 'Scan to add pass' }) {
    if (!win || win.closed) {
        return false;
    }

    try {
        win.document.open();
        win.document.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${escapeHtml(heading)}</title><style>${BASE_STYLE}</style></head><body><h1>${escapeHtml(heading)}</h1><div id="main"></div></body></html>`);
        win.document.close();
    } catch {
        return false;
    }

    const main = win.document.getElementById('main');
    if (!main) {
        return false;
    }

    if (qr && qr.startsWith('data:')) {
        main.innerHTML = `<div class="card"><img src="${escapeAttr(qr)}" width="320" height="320" alt="Wallet QR code" /></div>`;
        if (win.opener) {
            win.opener = null;
        }
        return true;
    }

    if (qr && qr.length <= MAX_QR_STRING_LENGTH) {
        const wrap = win.document.createElement('div');
        wrap.className = 'card';
        main.appendChild(wrap);
        const root = createRoot(wrap);
        root.render(
            <QRCodeSVG value={qr} size={320} level="L" boostLevel={false} marginSize={4} />
        );
        if (win.opener) {
            win.opener = null;
        }
        return true;
    }

    main.innerHTML = '';
    const p = win.document.createElement('p');
    p.textContent =
        'This add-to-wallet link is too long to encode as a QR code. Open the link on the customer’s phone or use the email or text they received.';
    main.appendChild(p);
    if (passAddUrl) {
        const a = win.document.createElement('a');
        a.href = passAddUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.textContent = 'Open add to wallet link';
        main.appendChild(a);
    }
    if (win.opener) {
        win.opener = null;
    }
    return true;
}

/**
 * Render wallet QR in a new tab and keep the merchant on the dashboard tab.
 * @param {{ qr: string | null, passAddUrl?: string | null, heading?: string }} opts
 * @returns {boolean} whether a tab was opened and updated
 */
export function openWalletQrInNewTab(opts) {
    const win = openWalletQrLoadingTab();
    if (!win) {
        return false;
    }
    const ok = renderWalletQrIntoWindow(win, opts);
    if (ok) {
        focusMerchantAppTab();
    }
    return ok;
}

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/'/g, '&#39;');
}
